import Link from "next/link";
import { MDXRemote } from "next-mdx-remote";

import { BlogListItem, CImage, Tag } from "../../components";
import {
  getBlogBySlug,
  getBlogSlugs,
  getBlogByCategory,
  getBlogRelations,
  getConfig,
} from "../../lib/api";
import { BlogPost, SocialLink } from "../../lib/models";
import { mdxComponents } from "../../lib/mdx";
import {
  getGlobalStaticProps,
  getShareLinks,
  ShareLinks,
} from "../../lib/helpers";
import { ShareIcons } from "../../components/share-icons";
import { stripHtml } from "../../lib/utils";
import { getIconPrefix } from "../../lib/helpers/get-icon-prefix";

interface BlogPageData {
  post: BlogPost;
  relatedPosts: BlogPost[];
  shareLinks: ShareLinks;
  about: string;
  socialLinks: SocialLink[];
}

export default function BlogPage({ data }: { data: BlogPageData }) {
  const {
    categoryFull,
    authorFull,
    title,
    contentMdx,
    minutes_read,
    image,
    tags,
    isMediumPost,
  } = data.post;
  const renderAuthor = () => (
    <div className="d-flex align-items-center">
      <CImage
        src={authorFull.image}
        alt={authorFull.title + " photo"}
        width={40}
        height={40}
        className="rounded-circle avatar-md"
      />
      <div className="ms-2 lh-1">
        <h5 className="mb-1">{authorFull.title}</h5>
        <span className="text-primary">{authorFull.job_title}</span>
      </div>
    </div>
  );

  return (
    <div className="bg-white">
      <div className="py-4 py-lg-8 pb-14">
        <div className="container">
          {/* Title, author etc. */}
          <div className="row">
            <div className="col-lg-8 pr-lg-6 mb-2">
              <div className="mb-4">
                <Link
                  href={categoryFull.slug}
                  className="font-size-sm fw-semi-bold d-block mb-4 text-primary"
                >
                  {categoryFull.title}
                </Link>
                <h1 className="display-3 fw-bold mb-4">{title}</h1>
                <span className="mb-3 d-inline-block">
                  {minutes_read} min read
                </span>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-5">
                {renderAuthor()}
                <ShareIcons links={data.shareLinks} />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-8 pr-lg-6 mb-6">
              {/* Main image */}
              <div className={isMediumPost ? "" : "mb-5"}>
                <CImage
                  src={image}
                  alt={"TODO"}
                  width={"100%"}
                  // height={500}
                  objectFit="contain"
                  className="rounded-lg position-relative"
                />
              </div>

              {/* Content */}
              <div>
                <MDXRemote
                  {...contentMdx}
                  components={{
                    ...mdxComponents,
                    img: (props) => {
                      // in a Medium post the "main" image is the same as the first image in the content,
                      // So we don't want to show the image twice
                      if (isMediumPost && props.src === image) return null;
                      return CImage({
                        ...props,
                        height: props.height || 500,
                        width: props.width || 950,
                        objectFit: "contain",
                      });
                    },
                  }}
                />
              </div>
              <hr className="mt-8 mb-5" />

              {/* Footer */}
              <div className="d-flex justify-content-between align-items-center mb-5">
                {renderAuthor()}
                <ShareIcons links={data.shareLinks} />
              </div>
              <div
                className="mb-4"
                style={{ display: "flex", flexWrap: "wrap" }}
              >
                {tags.map((t) => (
                  <Tag key={t} tag={t} />
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-lg-4">
              {/* About */}
              <div className="card mb-8 border">
                <div className="card-body p-4">
                  <h3>About CloudAnswers</h3>
                  <div>{data.about}</div>
                  <div className="mt-3">
                    {data.socialLinks.map((l) => (
                      <a
                        key={l.url}
                        href={l.url}
                        target="_blank"
                        className={`${getIconPrefix(l.icon)} ${
                          l.icon
                        } text-muted h2 me-2`}
                      ></a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Related Articles */}
              <div>
                <h3 className="mb-3">Related Articles</h3>
                {data.relatedPosts.map((p) => (
                  <div className="mb-4" key={p.slug}>
                    <BlogListItem {...p} isFullWidth={false} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export async function getStaticPaths() {
  const paths = await getBlogSlugs();
  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps({ params }) {
  const config = await getConfig();
  const post = await getBlogRelations(await getBlogBySlug(params.slug));
  const relatedPosts = await Promise.all(
    (
      await getBlogByCategory(post.categoryFull!.name)
    )
      .filter((p) => p.slug !== post.slug)
      .slice(0, 3)
      .map(getBlogRelations)
  );
  const data: BlogPageData = {
    post,
    relatedPosts,
    about: config.about,
    socialLinks: config.social_links,
    shareLinks: await getShareLinks(post.slug),
  };

  const props = await getGlobalStaticProps(
    { data },
    {
      pageTitle: post.title,
      pageDescription:
        post.meta_description || stripHtml(post.content).substr(0, 150),
      pageImage: post.image,
      publishDate: post.date,
    }
  );
  return { props };
}
